import get from 'lodash/get';
import { createSelector as reselectCreateSelector } from 'reselect';
import { Get } from 'type-fest';
import { CheckoutState } from '../../state';

interface MonitoredSelector {
    lastInputState: unknown;
    lastResolverArgs: Array<unknown>;
    lastResult: unknown;
}

let selectorDebug = false;
const monitoredSelectors: Record<string, MonitoredSelector> = {};
const globalEnv = typeof window === 'object' ? window : process;

/**
 * enableSelectorDebugging
 *
 * Use this function to enable enhanced selector debugging in your code.
 */
export const enableSelectorDebugging = () => {
    selectorDebug = true;
};

/**
 * enhancedCreateSelector
 *
 * This function is a wrapper around reselect's `createSelector()`
 * that enables debug logging and monitoring of selectors.
 *
 * Usage:
 *
 * To enable debugging you need to perform two actions:
 * - Call enableSelectorDebugging() before any createSelector() call
 *   (could also be on your app's index.js file to ensure all selectors are covered.)
 * - For the selectors you want to debug add a new last paramenter with a debug name
 *
 * Example:
 * ```js
 * import createSelector, {enableSelectorDebugging} from './utils/selector';
 *
 * // Call this before any selector you want to debug
 * enableSelectorDebugging();
 *
 * const mySelectorIWantToDebug = createSelector(
 *   (state) => state.a,
 *   (state) => state.b,
 *   (a, b) => a + b,
 *   'aPlusBSelector'  //<-- this is the debugName that will identify this selector.
 * );
 * ```
 *
 * On your console you should be notified when any of your monitored selectors is recalculated.
 * You can also add watch expressions on your debugger to monitor the global var `__SELECTORS__[selectorDebugName]`.
 */
export const createSelector = <
    ReturnValueType,
    Combiner extends (...params: Array<any>) => ReturnValueType,
>(
    ...createArgs: Array<
        ((state: CheckoutState) => unknown) | Combiner | string
    >
): ((state: CheckoutState) => ReturnValueType) => {
    const debugName =
        typeof createArgs[createArgs.length - 1] === 'string'
            ? (createArgs.pop() as unknown as string)
            : null;

    let selector: ReturnType<typeof reselectCreateSelector>;

    if (selectorDebug && debugName) {
        monitoredSelectors[debugName] = monitoredSelectors[debugName] || {};

        if (
            !(
                globalEnv as {
                    __SELECTORS__?: Record<string, MonitoredSelector>;
                }
            ).__SELECTORS__
        ) {
            (
                globalEnv as {
                    __SELECTORS__?: Record<string, MonitoredSelector>;
                }
            ).__SELECTORS__ = monitoredSelectors;
        }

        const dependencies = createArgs.slice(0, -1);
        const resolver = createArgs.slice(-1)[0] as (
            ...params: Parameters<typeof reselectCreateSelector>
        ) => ReturnValueType;
        let lastResolverArgs: Parameters<typeof reselectCreateSelector>;
        let wasRecomputed = false;
        const enhancedResolver = (
            ...resolverArgs: Parameters<typeof reselectCreateSelector>
        ) => {
            lastResolverArgs = resolverArgs;
            wasRecomputed = true;
            return resolver(...resolverArgs);
        };

        const newArgs = [
            ...dependencies,
            enhancedResolver,
        ] as unknown as Parameters<typeof reselectCreateSelector>;

        selector = reselectCreateSelector(...newArgs);

        return function createSelector(...selectorArgs: Array<any>) {
            const lastResult = selector(
                ...(selectorArgs as Parameters<typeof reselectCreateSelector>),
            ) as ReturnValueType;

            if (wasRecomputed) {
                Object.assign(monitoredSelectors[debugName], {
                    recomputations: selector.recomputations(),
                    prevInputState:
                        monitoredSelectors[debugName].lastInputState,
                    prevResolverArgs:
                        monitoredSelectors[debugName].lastResolverArgs,
                    prevResult: monitoredSelectors[debugName].lastResult,
                    lastInputState: selectorArgs[0],
                    lastResolverArgs,
                    lastResult,
                });

                /* eslint-disable no-console */
                console.groupCollapsed(` selector change ${debugName}`);
                console.log('details ', monitoredSelectors[debugName]);
                console.groupEnd();
                /* eslint-enable no-console */

                wasRecomputed = false;
            }

            return lastResult;
        };
    }

    return reselectCreateSelector(
        ...(createArgs as unknown as Parameters<typeof reselectCreateSelector>),
    ) as unknown as (state: CheckoutState) => ReturnValueType;
};

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
    U[keyof U];
type ExcludeEmpty<T> = T extends AtLeastOne<T> ? T : never;

export const getNamespaceValue = <
    Namespace,
    Path extends string,
    FallbackValue extends Get<ExcludeEmpty<Namespace>, Path>,
>(
    namespace: Namespace,
    keyPath: Path,
    defaultValue?: FallbackValue,
): FallbackValue extends Exclude<Get<ExcludeEmpty<Namespace>, Path>, undefined>
    ? Exclude<Get<ExcludeEmpty<Namespace>, Path>, undefined>
    : Get<ExcludeEmpty<Namespace>, Path> =>
    get(namespace, keyPath, defaultValue);
